// Port of ios/Sources/Calls/CallManager.swift — the one owner of a call, at PAGE scope (it outlives
// any screen). It drives CallSignalingSocket (the session), CallRtcSession (the media) and the
// tones, and folds everything into one `CallUiState` the UI reads through `useCallState()`.
// Exactly one call at a time — the plugin itself rejects a second join.
//
// The worker owns REST and the main socket (data/features/calls.ts): it hands this manager the
// active-call table as a `calls` signal (the ring decision and the banners read it), the plugin
// config at join, and the decline. Everything media-shaped is here, because only a page has
// RTCPeerConnection, getUserMedia and a speaker.
import { data } from '../../../bridge/client';
import type { CallPerson, CallsView } from '../../../data/features/calls';
import { incomingCallToRing } from '../../../core/activeCalls';
import { CallCameraChoice, type CallCamera } from '../../../core/callCameraChoice';
import { CallRoster, handRaised, type CallParticipant, type Roster } from '../../../core/callRoster';
import { CallEmoji, type CallSocketEvent } from '../../../core/callsCodec';
import { CallScreenPolicy, type CallPhase } from '../../../core/callScreenPolicy';
import { CallTones } from '../../../core/callTones';
import { t, tf } from '../../../i18n/i18n';
import { mediaUrls } from '../../media';
import { CallRtcSession } from './callRtcSession';
import { CallSignalingSocket } from './callSignalingSocket';
import { playChime, TonePlayer } from './tones';
import { voicePlayback } from '../voice/playback';

export interface IncomingCall {
  channelId: string;
  callId: string;
  callerId: string;
  callerName: string;
}

export type CallNotice = 'micDenied' | 'screenDenied' | 'callFailed' | 'callEnded' | 'alreadyInCall' | 'cameraDenied';

/** One emoji reaction floating over the call — a moment, not state. */
export interface CallReactionBurst {
  id: number;
  userId: string;
  sessionId: string;
  glyph: string;
  at: number;
}

export interface CallUiState {
  phase: CallPhase;
  channelId: string;
  channelName: string;
  startAt: number;
  muted: boolean;
  participants: CallParticipant[];
  people: Record<string, CallPerson>;
  sharingScreen: boolean;
  remoteScreenSharing: boolean;
  incoming: IncomingCall | null;
  notice: CallNotice | null;
  handRaised: boolean;
  reactions: CallReactionBurst[];
  sessionId: string;
  videoAllowed: boolean;
  cameraOn: boolean;
  cameras: CallCamera[];
  cameraId: string | null;
  /** The expanded huddle (the Mac's huddle WINDOW, here an in-page panel). */
  expanded: boolean;
  remoteScreen: MediaStream | null;
  remoteVideos: Record<string, MediaStream>;
  localVideo: MediaStream | null;
  /** The last few lifecycle events, timestamped (the Mac's diagnostics panel). */
  log: string[];
}

const idle = (): CallUiState => ({
  phase: 'idle',
  channelId: '',
  channelName: '',
  startAt: 0,
  muted: true,
  participants: [],
  people: {},
  sharingScreen: false,
  remoteScreenSharing: false,
  incoming: null,
  notice: null,
  handRaised: false,
  reactions: [],
  sessionId: '',
  videoAllowed: false,
  cameraOn: false,
  cameras: [],
  cameraId: null,
  expanded: false,
  remoteScreen: null,
  remoteVideos: {},
  localVideo: null,
  log: [],
});

export const NOTICE_KEYS: Record<CallNotice, string> = {
  micDenied: 'call.notice.micDenied',
  screenDenied: 'call.notice.screenDenied',
  callFailed: 'call.notice.failed',
  callEnded: 'call.notice.ended',
  alreadyInCall: 'call.notice.already',
  cameraDenied: 'call.notice.cameraDenied',
};

const REACTION_LIFETIME_MS = 4_000;
const RING_TIMEOUT_MS = 45_000;
const INCOMING_TAG = 'satori.incoming-call';

type Listener = () => void;

export class CallManager {
  static readonly reactionLifetimeMs = REACTION_LIFETIME_MS;

  private state: CallUiState = idle();
  private listeners = new Set<Listener>();
  private activeView: CallsView = { calls: [], channelTypes: {}, people: {}, unavailable: false };
  private activeListeners = new Set<Listener>();

  private me: string | null = null;
  private serverBase = '';
  private socket: CallSignalingSocket | null = null;
  private rtc: CallRtcSession | null = null;
  private roster: Roster = {};
  private iceServers: Awaited<ReturnType<typeof fetchConfig>>['iceServers'] = [];
  private waitingTone = new TonePlayer();
  private ringer = new TonePlayer();
  private sawPeerThisCall = false;
  private lastChimeAt = 0;
  private ringTimeout: ReturnType<typeof setTimeout> | null = null;
  private knownCallIds = new Set<string>();
  private incomingNotification: Notification | null = null;
  private audioElements = new Map<string, HTMLAudioElement>();
  private wakeLock: { release(): Promise<void> } | null = null;
  private reactionSeq = 0;
  private screenStream: MediaStream | null = null;
  private deviceListener: (() => void) | null = null;
  private wired = false;

  // MARK: - Store plumbing (useSyncExternalStore)

  getState = (): CallUiState => this.state;
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getActive = (): CallsView => this.activeView;
  subscribeActive = (listener: Listener): (() => void) => {
    this.activeListeners.add(listener);
    return () => this.activeListeners.delete(listener);
  };

  private set(patch: Partial<CallUiState>): void {
    const before = this.state;
    this.state = { ...before, ...patch };
    if (before.phase !== this.state.phase) this.applyScreenPolicy();
    for (const l of this.listeners) l();
  }

  /** Called once by CallHost: the worker's signals, page unload, and who "me" is. */
  wire(me: string | null, serverBase: string): void {
    this.me = me;
    this.serverBase = serverBase;
    if (this.wired) return;
    this.wired = true;
    data.onSignal((s) => {
      if (s.kind === 'calls') this.onActiveCallsChanged(s.view);
    });
    void data
      .call('callsState')
      .then((view) => this.onActiveCallsChanged(view, true))
      .catch(() => undefined);
    // Leaving cleanly when the tab closes: the server drops the session at once instead of after
    // its reconnect window, so nobody sees a ghost on the call for 20 s.
    window.addEventListener('pagehide', () => {
      this.socket?.leave();
      this.rtc?.close();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.applyScreenPolicy();
    });
    if (localStorage.getItem('satori.callDebug') === '1') {
      (globalThis as unknown as { __satoriCall: unknown }).__satoriCall = this;
    }
  }

  /** Debug/verification only: the live peer connection and the remote audio elements. */
  get debug(): { pc: RTCPeerConnection | null; audio: HTMLAudioElement[] } {
    return { pc: this.rtc?.pc ?? null, audio: [...this.audioElements.values()] };
  }

  logCallEvent(message: string): void {
    const time = new Date().toTimeString().slice(0, 8);
    this.set({ log: [...this.state.log, `${time} ${message}`].slice(-12) });
  }

  // MARK: - Joining and leaving

  join(channelId: string, channelName: string): void {
    if (this.state.phase !== 'idle') {
      if (this.state.channelId !== channelId) this.set({ notice: 'alreadyInCall' });
      return;
    }
    this.stopRinging();
    void (async () => {
      if (!(await this.ensureMicrophonePermission())) {
        this.set({ notice: 'micDenied', incoming: null });
        return;
      }
      await this.doJoin(channelId, channelName);
    })();
  }

  private async doJoin(channelId: string, channelName: string): Promise<void> {
    // A call takes the microphone and the speaker: a voice note being recorded PAUSES (the voice
    // feature listens for this event — its recorder.ts), one being played stops.
    window.dispatchEvent(new Event('satori:media-interrupt'));
    voicePlayback.stop();
    this.roster = {};
    this.sawPeerThisCall = false;
    this.state = { ...idle(), phase: 'joining', channelId, channelName, startAt: Date.now(), people: this.state.people };
    this.applyScreenPolicy();
    this.emit();
    // TURN/STUN before the socket; a failed fetch degrades to host candidates.
    const config = await fetchConfig();
    this.iceServers = config.iceServers;
    if (this.state.phase !== 'joining' || this.state.channelId !== channelId) return;
    this.set({ videoAllowed: config.videoEnabled });
    if (config.videoEnabled) {
      void this.refreshCameras();
      this.observeCameraHotplug();
    }
    const socket = new CallSignalingSocket(
      () => (this.serverBase || location.origin).replace(/^http/, 'ws') + '/api/v4/websocket',
      () => null,
      {
        joined: () => this.onSocketJoined(),
        signal: (json) => this.rtc?.handleRemoteSignal(json),
        callState: (json) => this.onCallState(json),
        rosterEvent: (event) => this.onRosterEvent(event),
        closed: (reason) => {
          this.logCallEvent(`socket closed reason=${reason ?? '-'} phase=${this.state.phase}`);
          if (this.state.phase !== 'idle') this.teardown('callFailed');
        },
        log: (line) => this.logCallEvent(line),
      },
    );
    this.socket = socket;
    socket.connect(channelId);
  }

  leave(): void {
    this.teardown(null);
  }

  acceptIncoming(): void {
    const incoming = this.state.incoming;
    if (!incoming) return;
    this.stopRinging();
    this.set({ incoming: null });
    this.join(incoming.channelId, incoming.callerName);
  }

  declineIncoming(): void {
    const incoming = this.state.incoming;
    if (!incoming) return;
    this.stopRinging();
    this.set({ incoming: null });
    void data.call('dismissCallNotification', incoming.channelId).catch(() => undefined);
  }

  consumeNotice(): void {
    if (this.state.notice) this.set({ notice: null });
  }

  setExpanded(expanded: boolean): void {
    if (this.state.expanded !== expanded) this.set({ expanded });
  }

  private teardown(notice: CallNotice | null): void {
    this.logCallEvent(`teardown notice=${notice ?? '-'} roster=${Object.keys(this.roster).length}`);
    this.sawPeerThisCall = false;
    this.waitingTone.stop();
    this.stopDeviceObservation();
    this.socket?.leave();
    this.socket = null;
    this.rtc?.close();
    this.rtc = null;
    this.screenStream?.getTracks().forEach((track) => track.stop());
    this.screenStream = null;
    this.state.localVideo?.getTracks().forEach((track) => track.stop());
    for (const audio of this.audioElements.values()) {
      audio.srcObject = null;
      audio.remove();
    }
    this.audioElements.clear();
    this.roster = {};
    const keep = { incoming: this.state.incoming, people: this.state.people, log: this.state.log };
    this.state = { ...idle(), ...keep, notice };
    this.applyScreenPolicy();
    this.emit();
    // An immediate hang-up can outrun the join's own broadcasts: re-read the server's table.
    void data
      .call('refreshCalls')
      .then((view) => this.onActiveCallsChanged(view))
      .catch(() => undefined);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  // MARK: - In-call controls

  toggleMute(): void {
    if (this.state.phase !== 'active' || !this.rtc) return;
    if (!this.state.muted) {
      this.applyMute(true);
      return;
    }
    // Unmuting is the first moment a call OPENS the microphone (joining only plays the other side).
    void (async () => {
      const rtc = this.rtc;
      if (!rtc) return;
      let fresh: MediaStreamTrack | undefined;
      if (!rtc.hasLiveMic) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
          fresh = stream.getAudioTracks()[0];
        } catch (error) {
          this.logCallEvent(`unmute refused: ${String(error)}`);
          this.set({ notice: 'micDenied' });
          return;
        }
      }
      if (this.rtc !== rtc || this.state.phase !== 'active') {
        fresh?.stop();
        return;
      }
      this.applyMute(false, fresh);
    })();
  }

  private applyMute(muted: boolean, fresh?: MediaStreamTrack): void {
    this.logCallEvent(muted ? 'mute' : 'unmute');
    void this.rtc?.setMuted(muted, fresh);
    this.socket?.sendMute(muted);
    this.set({ muted });
  }

  /** Granted/denied answered from the current state; only "prompt" actually asks — at JOIN, the
   *  apps' moment. The capture is released at once: a muted call holds no microphone. */
  private async ensureMicrophonePermission(): Promise<boolean> {
    if (!navigator.mediaDevices?.getUserMedia) return false;
    try {
      const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
      if (status?.state === 'granted') return true;
      if (status?.state === 'denied') return false;
    } catch {
      /* Firefox has no 'microphone' permission name: ask */
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch {
      return false;
    }
  }

  /** Re-reads the cameras and settles which one is "the" camera (CallCameraChoice). */
  async refreshCameras(): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[]);
    const cameras: CallCamera[] = devices
      .filter((d) => d.kind === 'videoinput' && d.deviceId)
      .map((d, i) => ({ id: d.deviceId, name: d.label || `${t('call.camera.short')} ${i + 1}`, position: CallCameraChoice.positionOf(undefined, d.label) }));
    let saved: string | null = this.state.cameraId;
    if (!saved) {
      try {
        saved = localStorage.getItem(CallCameraChoice.storageKey);
      } catch {
        saved = null;
      }
    }
    const preferFront = matchMedia('(pointer: coarse)').matches;
    const chosen = CallCameraChoice.resolve(cameras, saved, preferFront);
    const lost = this.state.cameraOn && this.state.cameraId !== null && cameras.length > 0 && chosen?.id !== this.state.cameraId;
    this.set({ cameras, cameraId: chosen?.id ?? null });
    // The camera in use was unplugged: carry on with the next one rather than freeze.
    if (lost && chosen) void this.switchRunningCamera(chosen.id);
  }

  private observeCameraHotplug(): void {
    if (this.deviceListener || !navigator.mediaDevices?.addEventListener) return;
    this.deviceListener = () => {
      if (this.state.phase !== 'idle') void this.refreshCameras();
    };
    navigator.mediaDevices.addEventListener('devicechange', this.deviceListener);
  }

  private stopDeviceObservation(): void {
    if (this.deviceListener) navigator.mediaDevices?.removeEventListener('devicechange', this.deviceListener);
    this.deviceListener = null;
  }

  private async openCamera(deviceId: string | null): Promise<MediaStreamTrack | null> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // A call-sized format (~640 px, 24 fps): every pixel is upload bandwidth and SFU CPU.
        video: { deviceId: deviceId ? { exact: deviceId } : undefined, width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 24, max: 24 } },
      });
      return stream.getVideoTracks()[0] ?? null;
    } catch {
      return null;
    }
  }

  /** The camera picker: switches a running camera live, or turns the camera on. Remembered. */
  selectCamera(id: string): void {
    try {
      localStorage.setItem(CallCameraChoice.storageKey, id);
    } catch {
      /* a per-viewer convenience */
    }
    this.set({ cameraId: id });
    if (this.state.cameraOn) void this.switchRunningCamera(id);
    else this.toggleCamera();
  }

  private async switchRunningCamera(id: string): Promise<void> {
    const track = await this.openCamera(id);
    if (!track || !this.rtc || !this.state.cameraOn) {
      track?.stop();
      if (!track) this.set({ notice: 'cameraDenied' });
      return;
    }
    const ok = await this.rtc.switchCamera(track);
    if (!ok) {
      track.stop();
      return;
    }
    this.set({ localVideo: new MediaStream([track]) });
  }

  /** `video_on` announces the stream id BEFORE the track (rtcd drops an unannounced video track). */
  toggleCamera(): void {
    const rtc = this.rtc;
    const socket = this.socket;
    if (this.state.phase !== 'active' || !this.state.videoAllowed || !rtc || !socket) return;
    if (this.state.cameraOn) {
      void rtc.stopCamera();
      socket.sendVideoOff();
      this.state.localVideo?.getTracks().forEach((track) => track.stop());
      this.set({ cameraOn: false, localVideo: null });
      return;
    }
    void (async () => {
      const track = await this.openCamera(this.state.cameraId);
      if (!track) {
        this.set({ notice: 'cameraDenied' });
        return;
      }
      if (this.rtc !== rtc || this.state.phase !== 'active' || this.state.cameraOn) {
        track.stop();
        return;
      }
      await this.refreshCameras(); // labels exist only once a camera was granted
      const settings = track.getSettings();
      const stream = new MediaStream([track]);
      socket.sendVideoOn(stream.id);
      this.set({ cameraOn: true, localVideo: stream, cameraId: settings.deviceId ?? this.state.cameraId });
      await rtc.startCamera(stream);
    })();
  }

  /** The self-view mirrors only a camera that faces the person. */
  get selfViewMirrored(): boolean {
    const camera = this.state.cameras.find((c) => c.id === this.state.cameraId) ?? null;
    const facing = this.state.localVideo?.getVideoTracks()[0]?.getSettings().facingMode;
    if (facing === 'environment') return false;
    return CallCameraChoice.mirrorsSelfView(camera);
  }

  /** ✋ — optimistic; the server's echo for this session confirms or corrects it. */
  toggleRaiseHand(): void {
    if (this.state.phase !== 'active') return;
    const raise = !this.state.handRaised;
    this.logCallEvent(raise ? 'raise hand' : 'lower hand');
    this.set({ handRaised: raise });
    this.socket?.sendRaiseHand(raise);
  }

  /** Shown here at once; the server's echo for this same session is then skipped. */
  sendReaction(name: string): void {
    const emoji = CallEmoji.fromName(name);
    if (this.state.phase !== 'active' || !emoji) return;
    this.socket?.sendReaction(emoji);
    this.addReaction(this.me ?? '', this.state.sessionId, CallEmoji.glyph(emoji));
  }

  private addReaction(userId: string, sessionId: string, glyph: string): void {
    const burst: CallReactionBurst = { id: ++this.reactionSeq, userId, sessionId, glyph, at: Date.now() };
    // A handful at once is a celebration; dozens is noise and a layout cost.
    this.set({ reactions: [...this.state.reactions, burst].slice(-12) });
    setTimeout(() => this.set({ reactions: this.state.reactions.filter((r) => r.id !== burst.id) }), REACTION_LIFETIME_MS);
  }

  /** The join/leave chime, for OTHER people only and never more than one per 0.6 s. */
  private chimeIfNeeded(event: CallSocketEvent): void {
    if (this.state.phase !== 'active') return;
    let kind: 'join' | 'leave';
    if (event.kind === 'userJoined') {
      if (event.sessionId === this.state.sessionId || this.roster[event.sessionId]) return;
      kind = 'join';
    } else if (event.kind === 'userLeft') {
      if (event.sessionId === this.state.sessionId || !this.roster[event.sessionId]) return;
      kind = 'leave';
    } else return;
    const now = Date.now();
    if (now - this.lastChimeAt <= CallTones.chimeGapMillis) return;
    this.lastChimeAt = now;
    playChime(kind);
  }

  // MARK: - Screen share (getDisplayMedia: the browser's own picker is the confirmation)

  requestScreenShare(): void {
    const rtc = this.rtc;
    const socket = this.socket;
    if (this.state.phase !== 'active' || this.state.sharingScreen || !rtc || !socket) return;
    void (async () => {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 15, max: 30 } }, audio: false });
      } catch {
        this.set({ notice: 'screenDenied' });
        return;
      }
      if (this.rtc !== rtc || this.state.phase !== 'active' || this.state.sharingScreen) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const track = stream.getVideoTracks()[0];
      // The browser's own "Stop sharing" bar ends the track: that is a stop like ours.
      track?.addEventListener('ended', () => this.stopScreenShare());
      this.screenStream = stream;
      socket.sendScreenOn(stream.id);
      this.set({ sharingScreen: true });
      await rtc.startScreenShare(stream);
    })();
  }

  stopScreenShare(): void {
    const stream = this.screenStream;
    this.screenStream = null;
    stream?.getTracks().forEach((track) => track.stop());
    if (!this.state.sharingScreen) return;
    void this.rtc?.stopScreenShare();
    this.socket?.sendScreenOff();
    this.set({ sharingScreen: false });
  }

  // MARK: - Incoming calls (ringing)

  /** Fed the active-call table every time it changes: NEW call in a DM/GM by someone else → ring. */
  private onActiveCallsChanged(view: CallsView, initial = false): void {
    this.activeView = view;
    for (const l of this.activeListeners) l();
    const previous = this.knownCallIds;
    this.knownCallIds = new Set(view.calls.map((c) => c.id));
    // The FIRST table the page sees is history, not news: a call already running when the tab
    // opened is shown by the banner and the row marker, never rung for.
    if (initial && previous.size === 0) return;

    const incoming = this.state.incoming;
    if (incoming && !view.calls.some((c) => c.id === incoming.callId)) {
      this.stopRinging();
      this.set({ incoming: null });
      return;
    }
    const fresh = incomingCallToRing({
      calls: view.calls,
      previousCallIds: previous,
      currentUserId: this.me,
      idle: this.state.phase === 'idle',
      alreadyRinging: !!this.state.incoming,
      channelType: (id) => view.channelTypes[id] ?? null,
    });
    if (!fresh) return;
    const callerName = view.people[fresh.ownerId]?.name ?? '';
    this.set({ incoming: { channelId: fresh.channelId, callId: fresh.id, callerId: fresh.ownerId, callerName } });
    this.startRinging();
    this.raiseIncomingAlert(callerName, fresh.ownerId);
  }

  private startRinging(): void {
    this.stopRinging(false);
    this.ringer.start('ring');
    this.ringTimeout = setTimeout(() => {
      this.stopRinging();
      this.set({ incoming: null });
    }, RING_TIMEOUT_MS);
  }

  private stopRinging(clearAlert = true): void {
    if (clearAlert) this.clearIncomingAlert();
    if (this.ringTimeout) clearTimeout(this.ringTimeout);
    this.ringTimeout = null;
    this.ringer.stop();
  }

  /**
   * IncomingCallAlert: the ring is only HEARD, and a tab is usually behind other windows — so a
   * call also raises a notification that reads as a call, silent (the loop is the sound), taken
   * down the instant the ring stops. Only while the page is not in front (in front, the card is
   * the alert), and only with the permission the notifications feature already asked for.
   */
  private raiseIncomingAlert(callerName: string, callerId: string): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible' && document.hasFocus()) return;
    try {
      const note = new Notification(callerName || t('call.incoming.title'), {
        body: callerName ? tf('call.incoming.from', callerName) : t('call.incoming.title'),
        icon: callerId ? mediaUrls.avatar(callerId, 0) : undefined,
        tag: INCOMING_TAG,
        requireInteraction: true,
        silent: true,
      } as NotificationOptions);
      note.onclick = () => {
        window.focus();
        note.close();
      };
      this.incomingNotification = note;
    } catch (error) {
      console.warn('[satori] incoming call notification', error);
    }
  }

  private clearIncomingAlert(): void {
    this.incomingNotification?.close();
    this.incomingNotification = null;
  }

  /** The ringback while this device is ALONE on a connected call; never again once a peer was here. */
  private updateWaitingTone(): void {
    if (Object.keys(this.roster).length > 1) this.sawPeerThisCall = true;
    const alone = this.state.phase === 'active' && Object.keys(this.roster).length <= 1 && !this.sawPeerThisCall;
    if (alone && !this.waitingTone.playing) this.waitingTone.start('ringback');
    else if (!alone) this.waitingTone.stop();
  }

  private publishRoster(): void {
    const participants = Object.values(this.roster).sort((a, b) => (a.sessionId < b.sessionId ? -1 : a.sessionId > b.sessionId ? 1 : 0));
    const people = { ...this.activeView.people, ...this.state.people };
    const missing = [...new Set(participants.map((p) => p.userId).filter((id) => id && !people[id]))];
    if (missing.length) {
      void data
        .call('callPeople', missing)
        .then((found) => {
          if (Object.keys(found).length) this.set({ people: { ...this.state.people, ...found } });
        })
        .catch(() => undefined);
    }
    this.set({
      participants,
      people,
      remoteScreenSharing: !!this.state.remoteScreen || participants.some((p) => p.sharingScreen && p.sessionId !== this.state.sessionId),
    });
    this.updateWaitingTone();
  }

  // MARK: - Socket events

  private onSocketJoined(): void {
    if (this.rtc) return;
    const own = this.socket?.sessionId;
    if (own) this.set({ sessionId: own });
    const rtc = new CallRtcSession(this.iceServers, {
      localSdp: (json) => this.socket?.sendSdp(json),
      localCandidate: (json) => this.socket?.sendIce(json),
      connected: () => {
        if (this.state.phase === 'joining') {
          this.set({ phase: 'active' });
          this.updateWaitingTone();
        }
      },
      remoteScreen: (stream) => {
        if (stream === this.state.remoteScreen) return;
        // The Mac's huddle window opens by itself when somebody starts sharing.
        this.set({ remoteScreen: stream, remoteScreenSharing: !!stream, expanded: stream && !this.state.remoteScreen ? true : this.state.expanded });
      },
      remoteVideos: (bySession) => {
        const before = this.state.remoteVideos;
        const changed = Object.keys(bySession).length !== Object.keys(before).length || Object.entries(bySession).some(([k, v]) => before[k] !== v);
        if (changed) this.set({ remoteVideos: bySession });
      },
      remoteAudio: (track) => this.playRemoteAudio(track),
      connectionState: (state) => this.logCallEvent(`rtc state=${state}`),
      failed: (reason) => {
        this.logCallEvent(`rtc failed reason=${reason}`);
        if (this.state.phase !== 'idle') this.teardown('callFailed');
      },
    });
    this.rtc = rtc;
    rtc.start();
  }

  /** Remote voices play through hidden <audio> elements (a page has no audio unit of its own). */
  private playRemoteAudio(track: MediaStreamTrack): void {
    if (this.audioElements.has(track.id)) return;
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.dataset.callAudio = track.id;
    audio.style.display = 'none';
    audio.srcObject = new MediaStream([track]);
    document.body.appendChild(audio);
    void audio.play().catch((error) => this.logCallEvent(`audio play blocked: ${String(error)}`));
    this.audioElements.set(track.id, audio);
    track.addEventListener('ended', () => {
      audio.srcObject = null;
      audio.remove();
      this.audioElements.delete(track.id);
    });
  }

  private onCallState(callJson: string): void {
    const parsed = CallRoster.fromCallStateJson(callJson);
    // An UNPARSEABLE snapshot must not wipe a roster that events built.
    if (!Object.keys(parsed).length && Object.keys(this.roster).length) {
      this.logCallEvent(`call_state parsed EMPTY (kept roster=${Object.keys(this.roster).length})`);
    } else {
      this.roster = parsed;
    }
    const own = this.state.sessionId ? this.roster[this.state.sessionId] : undefined;
    if (own) this.set({ handRaised: handRaised(own) });
    this.publishRoster();
  }

  private onRosterEvent(event: CallSocketEvent): void {
    if (event.kind === 'callEnd') {
      this.teardown('callEnded');
      return;
    }
    if (!this.state.sessionId && this.socket?.sessionId) this.set({ sessionId: this.socket.sessionId });
    if (event.kind === 'reacted') {
      if (event.sessionId !== this.state.sessionId || !this.state.sessionId) this.addReaction(event.userId, event.sessionId, CallEmoji.glyph(event.emoji));
      return;
    }
    if (event.kind === 'handState' && event.sessionId === this.state.sessionId) this.set({ handRaised: event.raisedAt > 0 });
    this.chimeIfNeeded(event);
    this.roster = CallRoster.apply(this.roster, event);
    this.publishRoster();
  }

  // MARK: - Screen awake (CallScreenPolicy through the Screen Wake Lock)

  private applyScreenPolicy(): void {
    const policy = CallScreenPolicy.decide(this.state.phase, 'speaker');
    const wakeLock = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock;
    if (policy.keepScreenAwake && !this.wakeLock && wakeLock) {
      void wakeLock
        .request('screen')
        .then((lock) => {
          if (this.state.phase === 'idle') void lock.release();
          else {
            this.wakeLock = lock;
            // The browser drops the lock whenever the tab is hidden; `wire` re-asks on return.
            (lock as unknown as EventTarget).addEventListener?.('release', () => {
              if (this.wakeLock === lock) this.wakeLock = null;
            });
          }
        })
        .catch(() => undefined);
    } else if (!policy.keepScreenAwake && this.wakeLock) {
      void this.wakeLock.release().catch(() => undefined);
      this.wakeLock = null;
    }
  }
}

function fetchConfig() {
  return data.call('callsConfig').catch(() => ({ iceServers: [], videoEnabled: false }));
}

export const callManager = new CallManager();
